import type { TFunction } from 'i18next';
import type {
  NotificationAudience,
  NotificationChannel,
  NotificationModuleTypeView,
} from '../../api/General/NotificationModules';
import { NOTIFICATION_TYPE_LABELS } from './NotificationPreferenceMatrix';

/**
 * CO-375 / E13 — every word of the opt-in prompt is COMPOSED from the catalog enums the server sends
 * (cadence / audience / channels) plus translated keys. No prose is hand-written per module, so the copy
 * cannot drift from what the senders actually do. Lists are joined with Intl.ListFormat in the active
 * language (Arabic "و", French "et"), never with a hard-coded ", ".
 */
const AUDIENCE_ORDER: NotificationAudience[] = ['employee', 'approver', 'hr', 'operator'];
const CHANNEL_ORDER: NotificationChannel[] = ['inApp', 'push', 'email'];

export function moduleTitle(t: TFunction, key: string): string {
  return t(`notificationModules.${key}.title`, { defaultValue: key });
}

export function typeLabel(t: TFunction, name: string): string {
  const label = (NOTIFICATION_TYPE_LABELS as Record<string, string | undefined>)[name];
  return label ? t(label, { nsSeparator: false }) : name;
}

function joinList(items: string[], lang: string): string {
  try {
    return new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

export function audiencesOf(types: NotificationModuleTypeView[]): NotificationAudience[] {
  const set = new Set(types.flatMap((x) => x.audience));
  // Operators are never a company-module audience worth naming to HR.
  return AUDIENCE_ORDER.filter((a) => a !== 'operator' && set.has(a));
}

export function channelsOf(types: NotificationModuleTypeView[]): NotificationChannel[] {
  const set = new Set(types.flatMap((x) => x.channels));
  return CHANNEL_ORDER.filter((c) => set.has(c));
}

/** "12 notifications to employees, approvers and HR, by in-app, push and email." */
export function moduleSummary(t: TFunction, lang: string, types: NotificationModuleTypeView[]): string {
  const audience = joinList(audiencesOf(types).map((a) => t(`notificationModules.audience.${a}`)), lang);
  const channels = joinList(channelsOf(types).map((c) => t(`notificationModules.channel.${c}`)), lang);
  return t('notificationModules.prompt.summary', { count: types.length, audience, channels });
}

export function cadenceLabel(t: TFunction, cadence: string): string {
  return t(`notificationModules.cadence.${cadence}`);
}

export function channelLabel(t: TFunction, channel: string): string {
  return t(`notificationModules.channel.${channel}`);
}

/**
 * Longest route-prefix match at a PATH BOUNDARY (exact, or followed by "/"), so /profile can never claim
 * /profile-settings and /payroll never claims /payroll-reports by accident (it lists it explicitly). The
 * people module's "/users" also matches the optional company segment of "/:company?/users".
 */
export function matchModuleForPath<T extends { webRoutePrefixes: string[] }>(
  modules: T[],
  pathname: string
): T | null {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const matches = (p: string) =>
    path === p ||
    path.startsWith(p + '/') ||
    (p === '/users' && /^\/[^/]+\/users(\/|$)/.test(path));
  let best: T | null = null;
  let bestLen = 0;
  for (const m of modules) {
    for (const prefix of m.webRoutePrefixes) {
      const p = prefix.toLowerCase();
      if (matches(p) && p.length > bestLen) {
        best = m;
        bestLen = p.length;
      }
    }
  }
  return best;
}
